/**
 * Identity Authentication Provider
 * React Context Provider for Identity-based authentication
 * Manages authentication state across the application
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { identityAuth, SessionStatus } from './IdentityAuth';
import { setAuthProviderInstance } from './errorHandler';

// Create authentication context
const IdentityAuthContext = createContext();

// Custom hook to use authentication context
export const useIdentityAuth = () => {
  const context = useContext(IdentityAuthContext);
  if (!context) {
    throw new Error('useIdentityAuth must be used within an IdentityAuthProvider');
  }
  return context;
};

const toUser = (userInfo) => ({
  // Basic user info
  userId: userInfo.userId,
  email: userInfo.email,
  firstName: userInfo.name ? userInfo.name.split(' ')[0] : '',
  lastName: userInfo.name ? userInfo.name.split(' ').slice(1).join(' ') : '',
  name: userInfo.name,
  roles: userInfo.roles || [],
  userType: userInfo.userType,

  // Organization info
  groups: userInfo.groups || [],
  schools: userInfo.schools || [],
  isMat: userInfo.isMat || false,
  matGroupId: userInfo.matGroupId,
  schoolId: userInfo.schoolId,

  // Access control info
  accessControl: userInfo.accessControl || {},

  // Token metadata
  claimsVersion: userInfo.claimsVersion,
  issuedAt: userInfo.issuedAt,
  expiresAt: userInfo.expiresAt
});

// Authentication Provider Component
export const IdentityAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        
        // Stays loading through transient Identity failures; false only once the session is confirmed gone
        const isAuth = await identityAuth.establishSession();
        setAuthenticated(isAuth);

        if (isAuth) {
          const userInfo = identityAuth.getUserInfo();
          
          if (userInfo) {
            setUser(toUser(userInfo));
            
            // Start periodic session checks
            identityAuth.startSessionCheck();
          } else {
            setAuthenticated(false);
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        setAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Follows checks nobody awaits: a held-back redirect whose re-check recovers, or a sign-out confirmed by polling
    const unsubscribe = identityAuth.subscribe((status) => {
      const userInfo = status === SessionStatus.Active ? identityAuth.getUserInfo() : null;
      setAuthenticated(Boolean(userInfo));
      setUser(userInfo ? toUser(userInfo) : null);
      if (userInfo) identityAuth.startSessionCheck();
    });
    
    // Register this provider instance with the error handler
    setAuthProviderInstance({ handleSessionExpired });
    
    // Cleanup on unmount
    return () => {
      unsubscribe();
      identityAuth.stopSessionCheck();
      setAuthProviderInstance(null);
    };
  }, []);

  const login = async (loginResponse) => {
    try {
      // IdentityAuth handles login via Identity app
      // This method is mainly for compatibility
      const isAuth = await identityAuth.checkAuth();
      setAuthenticated(isAuth);
      
      if (isAuth) {
        const userInfo = identityAuth.getUserInfo();
        if (userInfo) {
          setUser(toUser(userInfo));
        }
      }
      
      return isAuth;
    } catch (error) {
      return false;
    }
  };

  const logout = async () => {
    try {
      await identityAuth.logout();
      setAuthenticated(false);
      setUser(null);
    } catch (error) {
      setAuthenticated(false);
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const isAuth = await identityAuth.checkAuth();
      setAuthenticated(isAuth);
      
      if (isAuth) {
        const userInfo = identityAuth.getUserInfo();
        if (userInfo) {
          setUser(toUser(userInfo));
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      setAuthenticated(false);
      setUser(null);
    }
  };

  const hasRole = (role) => {
    return identityAuth.hasRole(role);
  };

  const getToken = () => {
    return identityAuth.getToken();
  };

  const fetchWithAuth = (url, options) => {
    return identityAuth.fetchWithAuth(url, options);
  };

  // An API 401 is only a hint: Identity confirms the loss (and redirects) through the
  // usual session check, and the subscription above updates state if it does.
  const handleSessionExpired = () => {
    void identityAuth.checkSession();
  };

  const checkForSessionExpired = (error) => {
    // Check if error indicates session expired
    const isSessionExpired = error && (
      // Check response data
      (error.response && error.response.data && (
        (typeof error.response.data === 'string' && error.response.data.includes('Session expired')) ||
        (typeof error.response.data === 'object' && error.response.data.error && error.response.data.error.includes('Session expired'))
      )) ||
      // A 403 is a permission answer, not a session one
      (error.response && error.response.status === 401) ||
      // Check error message
      (error.message && error.message.includes('Session expired'))
    );

    if (isSessionExpired) {
      handleSessionExpired();
      return true;
    }
    
    return false;
  };

  const value = {
    user,
    loading,
    authenticated,
    login,
    logout,
    refreshUser,
    hasRole,
    getToken,
    fetchWithAuth,
    handleSessionExpired,
    checkForSessionExpired,
  };

  return (
    <IdentityAuthContext.Provider value={value}>
      {children}
    </IdentityAuthContext.Provider>
  );
};
